import { z } from 'zod';
import { TenantIdentityRequestSchema, TenantSummarySchema } from '../../../../entities/tenant/tenant.schema';
import { ObjectIdSchema } from '../../../../shared/object-id.schema';
import { PasswordSchema } from '../../../../shared/password-validation';
/**
 * Clone Tenant Request Schema
 * POST /api/v2/tenants/:tenantId/clone
 *
 * The source tenant id is carried in the path; this body describes the *new*
 * tenant's identity (the clone copies the source database verbatim). The
 * discriminated union carries the demo pairing in the type itself: a demo
 * clone REQUIRES a demoPassword, a non-demo clone cannot have one.
 */ export const CloneTenantRequestSchema = z.discriminatedUnion('tenantType', [
    TenantIdentityRequestSchema.extend({
        tenantType: z.literal('demo'),
        /**
     * Shared sign-in password for every user of a demo clone. Demo clones
     * rewrite all user emails to fresh demo addresses and import the users
     * into Zitadel with this password, so the clone never depends on the
     * source tenant's credentials. PasswordSchema mirrors the Zitadel org
     * policy (upper/lower/digit) — a weaker password would only fail deep
     * inside the post-clone import.
     */ demoPassword: PasswordSchema
    }),
    TenantIdentityRequestSchema.extend({
        tenantType: z.enum([
            'partner',
            'customer'
        ])
    })
]);
/**
 * Clone Tenant Response Schema
 * Returned on 202 Accepted — the request is acknowledged, not finished.
 *
 * A clone takes ~65 s on a fat tenant, so only the synchronous part happens
 * inside the request (source lookup, slug availability, the tenant row and the
 * job document); the copy itself runs detached. Contains the newly created
 * tenant (the clone), not the source — born `active: false` and flipped to
 * active only once the clone completes.
 *
 * Progress and terminal outcome live on the job, polled via
 * `GET /api/v2/tenant-clone-jobs` (`TenantCloneJobSchema`), whose `status`
 * carries the outcome the response used to flag inline.
 */ export const CloneTenantResponseSchema = z.object({
    jobId: ObjectIdSchema,
    tenant: TenantSummarySchema
});
/**
 * Clone Tenant Error Schema
 * Standard error information including status code, message, and error type.
 */ export const CloneTenantErrorSchema = z.object({
    statusCode: z.number(),
    message: z.string(),
    error: z.string()
});

//# sourceMappingURL=clone-tenant.schema.js.map